// Sdileny hook pro nacteni seznamu klientu (jmena) pro autocomplete.
// localStorage cache 24h + retry 3x + persist mezi navigacemi i restarty browseru.
//
// Pouziti:
//   import { useClientsList, prewarmClientsList } from "../lib/clientsCache.js";
//   const klientOptions = useClientsList();  // string[] (sorted)
//
// Per-mount: pokud cache valid, vrati instantne. Pak fetch fresh na pozadi (stale-while-revalidate).
//
// 19.5.2026 optimalizace:
//   1. Endpoint /api/clients?light=names → vraci jen jmena (100-200ms vs 1700ms).
//   2. sessionStorage → localStorage (persistuje pres restart browseru).
//   3. TTL 10min → 24h (jmena se meni minimalne).
//   4. Pre-warm: prewarmClientsList() volame z Layout.jsx hned po auth → cache existuje
//      jeste pred otevrenim Log ST/Cash/Expired dialogu.

import { useEffect, useState } from "react";
import { apiFetch } from "./api.js";

const CACHE_KEY = "fw40_clients_names_cache_v2"; // _v2 — invalidace stareho sessionStorage formatu
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hodin

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { at, names } = JSON.parse(raw);
    if (!at || !Array.isArray(names)) return null;
    if (Date.now() - at > CACHE_TTL_MS) return null;
    return names;
  } catch {
    return null;
  }
}

function writeCache(names) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), names }));
  } catch {
    // localStorage full nebo SSR — ignoruj
  }
}

async function fetchWithRetry(url, retries = 3) {
  let lastErr;
  for (let i = 0; i < retries; i++) {
    try {
      const r = await apiFetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return await r.json();
    } catch (e) {
      lastErr = e;
      if (i < retries - 1) {
        await new Promise((res) => setTimeout(res, 300 * Math.pow(2, i)));
      }
    }
  }
  throw lastErr;
}

// Vlastni fetch + write cache. Pouzity z useClientsList i prewarmClientsList.
// Async safe: vola se i z Layout (top of app) i z hooku (per-page).
let _inflightPromise = null;
async function fetchAndCache() {
  if (_inflightPromise) return _inflightPromise;
  _inflightPromise = (async () => {
    try {
      const j = await fetchWithRetry("/api/clients?light=names");
      const arr = Array.isArray(j) ? j : (j.clients || []);
      const fresh = [...new Set(arr.map((c) => c.klient).filter(Boolean))].sort((a, b) => a.localeCompare(b, "cs"));
      if (fresh.length > 0) writeCache(fresh);
      return fresh;
    } finally {
      _inflightPromise = null;
    }
  })();
  return _inflightPromise;
}

// Pre-warm cache — volat z Layout.jsx po auth. Background fetch, neblokuje render.
export function prewarmClientsList() {
  if (typeof window === "undefined") return;
  const cached = readCache();
  if (cached && cached.length > 0) {
    // Mame validni cache → jen tichy refresh na pozadi pro pristi pouziti.
    fetchAndCache().catch(() => {});
    return;
  }
  // Zadny cache → spust fetch (vysledek se ulozi do localStorage, useClientsList ho pak najde).
  fetchAndCache().catch((e) => console.warn("[prewarmClientsList] fail:", e && e.message));
}

export function useClientsList() {
  const [names, setNames] = useState(() => readCache() || []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fresh = await fetchAndCache();
        if (cancelled) return;
        if (fresh.length > 0) setNames(fresh);
      } catch (e) {
        console.warn("[useClientsList] fetch failed after retries:", e.message);
        // Pokud uz mame nazvy v state (z cache), nech je, neprep nuluj
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return names;
}
